// src/components/popups/ShareDrawer.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './ShareDrawer.css';
import api from '../../api';
import profilePicPlaceholder from '../../assets/placeholder-profile.png';
import repostIcon from '../../assets/repost-icon.png';

function ShareDrawer({ isOpen, onClose, postId, sender, onShared }) {
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [loadingSuggested, setLoadingSuggested] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sending, setSending] = useState(false);

  const [repostText, setRepostText] = useState('');
  const [reposting, setReposting] = useState(false);
  const [senderProfile, setSenderProfile] = useState(null);

  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchSuggestedUsers();
      fetchSenderProfile();
    } else {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedUsers([]);
      setRepostText('');
      setReposting(false);
      setSending(false);
      setSuccessMsg('');
      setLoadingSuggested(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const body = document.body;

    if (isOpen) {
      const prevOverflow = body.style.overflow;

      body.style.overflow = 'hidden';
      body.classList.add('bf-share-drawer-open');

      return () => {
        body.style.overflow = prevOverflow;
        body.classList.remove('bf-share-drawer-open');
      };
    }
  }, [isOpen]);

  const fetchSuggestedUsers = async () => {
    try {
      setLoadingSuggested(true);

      const res = await api.get('/users/suggested');
      setSuggestedUsers(res.data || []);
    } catch (err) {
      console.error('Error fetching suggested users:', err);
      setSuggestedUsers([]);
    } finally {
      setLoadingSuggested(false);
    }
  };

  const fetchSenderProfile = async () => {
    if (!sender) return;

    try {
      const res = await api.get(`/users/${encodeURIComponent(sender)}`);
      setSenderProfile(res.data || null);
    } catch (err) {
      console.error('Error fetching sender profile:', err);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (query.length > 1) {
      try {
        const res = await api.get(`/users/search?q=${encodeURIComponent(query)}`);
        setSearchResults(res.data || []);
      } catch (err) {
        console.error('Search error:', err);
      }
    } else {
      setSearchResults([]);
    }
  };

  const toggleSelect = (user) => {
    const already = selectedUsers.find((u) => u._id === user._id);

    if (already) {
      setSelectedUsers(selectedUsers.filter((u) => u._id !== user._id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleSend = async () => {
    if (selectedUsers.length === 0 || sending) return;

    try {
      setSending(true);

      const res = await api.post('/share/send', {
        postId,
        recipients: selectedUsers.map((u) => u._id),
        sender,
      });

      console.log('Shared post:', res.data);
      onShared?.();
      onClose();
    } catch (err) {
      console.error('Share error:', err);
      alert('Failed to share. Try again.');
    } finally {
      setSending(false);
    }
  };

  const handleRepost = async () => {
    if (!sender || reposting || successMsg) return;

    try {
      setReposting(true);

      const res = await api.post(`/posts/${postId}/repost`, {
        username: sender,
        comment: repostText || '',
      });

      const newPost = res.data;
      console.log('Repost created:', newPost);

      window.dispatchEvent(
        new CustomEvent('post:created', { detail: { post: newPost } })
      );

      onShared?.(newPost);

      setSuccessMsg('Post reposted ');

      setTimeout(() => {
        onClose();
      }, 1600);
    } catch (err) {
      console.error('Repost error:', err);
      alert('Failed to repost.');
    } finally {
      setReposting(false);
    }
  };

  const renderUser = (user) => (
    <div
      key={user._id}
      className={`share-user ${
        selectedUsers.some((u) => u._id === user._id) ? 'selected' : ''
      }`}
      onClick={() => toggleSelect(user)}
    >
      <img src={user.profilePic || profilePicPlaceholder} alt="pic" />
      <div className="user-fullname">
        {user.name || user.username || 'Pioneer'}
      </div>
    </div>
  );

  const renderUserSkeletons = () =>
    Array.from({ length: 6 }).map((_, index) => (
      <div
        key={`share-user-skeleton-${index}`}
        className="share-user share-user-skeleton"
      >
        <div className="share-user-skeleton-avatar" />
        <div className="share-user-skeleton-name" />
      </div>
    ));

  if (!isOpen) return null;

  const avatarSrc =
    senderProfile?.profilePic?.startsWith('http')
      ? senderProfile.profilePic
      : profilePicPlaceholder;

  const senderName = senderProfile?.name || sender || 'You';

  const isMerchant =
    senderProfile?.userType === 'merchant' &&
    (senderProfile?.isMerchant || senderProfile?.user?.isMerchant);

  const isAdmin =
    senderProfile?.isAdmin === true || !!senderProfile?.adminRole;

  const isApp =
    senderProfile?.isApp === true || senderProfile?.accountType === 'app';

  const roleLabel = isAdmin
    ? 'Admin'
    : isApp
    ? 'App'
    : isMerchant
    ? 'Merchant'
    : 'Pioneer';

  const roleClass = roleLabel === 'Pioneer' ? 'pioneer-pill' : 'merchant-pill';

return createPortal(
  <div className="share-drawer">

      <div className="drawer-header">
        <span>Share</span>
        <button className="share-drawer-cancel" onClick={onClose}>
          Cancel
        </button>
      </div>

      <div className="share-repost-box">
        <div className="share-repost-header">
          <img src={avatarSrc} alt="avatar" className="share-repost-avatar" />

          <div className="share-repost-meta">
            <div className="share-repost-name">{senderName}</div>

            <div className="share-repost-role-row">
              <span className={roleClass}>{roleLabel}</span>
            </div>
          </div>
        </div>

        <textarea
          className="share-repost-input"
          placeholder="Say something..."
          value={repostText}
          onChange={(e) => setRepostText(e.target.value)}
          rows={3}
        />

        <div className="share-repost-actions">
          <button
            className="share-repost-button"
            onClick={handleRepost}
            disabled={reposting || !sender || !!successMsg}
          >
            {successMsg ? (
              'Done'
            ) : reposting ? (
              'Posting…'
            ) : (
              <>
                <img src={repostIcon} alt="" className="share-repost-icon" />
                <span>Repost</span>
              </>
            )}
          </button>

          {successMsg && <div className="share-success">{successMsg}</div>}
        </div>
      </div>

      <div className="share-divider" />

      <div className="share-section-title">Send DM</div>

      <input
        className="search-input"
        type="text"
        placeholder="Search users..."
        value={searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
      />

      <div className="user-list">
        {searchQuery.length > 1
          ? searchResults.map(renderUser)
          : loadingSuggested && suggestedUsers.length === 0
          ? renderUserSkeletons()
          : suggestedUsers.map(renderUser)}
      </div>

      {selectedUsers.length > 0 && (
        <button className="send-button" onClick={handleSend} disabled={sending}>
          {sending
            ? 'Sending…'
            : `Send to ${selectedUsers.length} user${
                selectedUsers.length > 1 ? 's' : ''
              }`}
        </button>
      )}

  </div>,
  document.body
);
}

export default ShareDrawer;
